import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Spinner,
} from "reactstrap";
import { useSelector } from "react-redux";
import moment from "moment";
import ReactApexChart from "react-apexcharts";

import { BreadCrumb } from "../../components/Common";
import {
  AdminAnalyticsResponse,
  AdminAnalyticsRequest,
  UserRoleWiseCount,
  UserPlatformWiseCount,
  UserRolePlatformWiseCount,
  CourseGradeWiseSubscriptions,
} from "../../interfaces/analytics";
import { APP_PLATFORM, RoleEnum } from "../../enums";
import Section from "./Section";
import { Widget } from "./Widgets";
import { useGetAdminAnalyticsMutation } from "../../store/api/analyticsApi";
import { AccountState } from "../../store/slices/accountSlice";

const DashboardPage = () => {
  const { t, i18n } = useTranslation();
  const profile = useSelector(
    (state: { account: AccountState }) => state.account.userProfile,
  );
  const isSuperAdmin = profile?.role?.key === RoleEnum.SUPER_ADMIN;

  const [
    getAdminAnalyticsApi,
    { isLoading: isLoadingAdminAnalytics, data: dataAdminAnalytics },
  ] = useGetAdminAnalyticsMutation();

  const [parentWidgets, setParentWidgets] = useState<any[]>([]);
  const [studentWidgets, setStudentWidgets] = useState<any[]>([]);
  const [otherWidgets, setOtherWidgets] = useState<any[]>([]);
  const [amountWidgets, setAmountWidgets] = useState<any[]>([]);
  const [dateValue, setDateValue] = useState([
    moment(new Date()).format("yyyy-MM-DD"),
    moment(new Date()).format("yyyy-MM-DD"),
  ]);

  const [query, setQuery] = useState<AdminAnalyticsRequest>({
    from: moment(new Date()).format("yyyy-MM-DD"),
    to: moment(new Date()).format("yyyy-MM-DD"),
  });

  const [isFirstLoad, setIsFirstLoad] = useState<boolean>(false);

  document.title = `${t("documentTitle", { pageName: "dashboard" })}`;

  // Refetch whenever filters (query) change so widgets update
  useEffect(() => {
    if (!isSuperAdmin) return;
    getAdminAnalyticsApi(query);
  }, [query, getAdminAnalyticsApi, isSuperAdmin]);

  const onChangeDate = (dates: any) => {
    if (dates && dates.length === 2) {
      setDateValue(dates);
      setQuery({
        from: moment(new Date(dates[0])).format("yyyy-MM-DD"),
        to: moment(new Date(dates[1])).format("yyyy-MM-DD"),
      });
    } else {
      setQuery({
        from: "",
        to: "",
      });
    }
  };

  useEffect(() => {
    if (!isSuperAdmin || !dataAdminAnalytics) return;
    if (!isFirstLoad) setIsFirstLoad(true);
    prepareWidgets(dataAdminAnalytics);
  }, [dataAdminAnalytics, i18n.language, isSuperAdmin]);

  const prepareWidgets = (data: AdminAnalyticsResponse) => {
    //#region widgets

    const amountWidgets: any[] = [];
    let sharedIndex = 0;

    // total amount
    amountWidgets.push({
      id: sharedIndex,
      label: t("totalAmountTitle"),
      description: t("totalAmountDescription"),
      counter: data.totalAmount,
      link: "null",
      linkCaption: "null",
      bgcolor: "secondary",
      icon: <i className="ri-wallet-3-line"></i>,
    });
    sharedIndex += 1;

    // total due deposit
    amountWidgets.push({
      id: sharedIndex,
      label: t("totalDueDepositTitle"),
      description: t("totalDueDepositDescription"),
      counter: data.totalDueDeposit,
      link: "null",
      linkCaption: "null",
      bgcolor: "secondary",
      icon: <i className="ri-wallet-3-line"></i>,
    });
    sharedIndex += 1;

    setAmountWidgets(amountWidgets);

    const parentWidgets: any[] = [];
    //  all parents
    data?.usersCount.forEach((value: UserRoleWiseCount) => {
      if ([RoleEnum.PARENT].includes(value.role as RoleEnum)) {
        parentWidgets.push({
          id: sharedIndex,
          label: t("allParents"),
          description: t("allParents"),
          counter: value.count,
          link: "null",
          linkCaption: "null",
          bgcolor: "secondary",
          icon: <i className="ri-parent-line"></i>,
        });
        sharedIndex += 1;
      }
    });

    // parents with at least 1 student account
    parentWidgets.push({
      id: sharedIndex,
      label: t("parentsHaveChildrenTitle"),
      description: t("parentsHaveChildrenDescription"),
      counter: data.parentsHavingChildren,
      link: "null",
      linkCaption: "null",
      bgcolor: "secondary",
      icon: <i className="ri-parent-line"></i>,
    });
    sharedIndex += 1;

    // parents with at least 1 student purchased
    parentWidgets.push({
      id: sharedIndex,
      label: t("parentsMadePurchaseTitle"),
      description: t("parentsMadePurchaseDescription"),
      counter: data.parentsWithPurchase,
      link: "null",
      linkCaption: "null",
      bgcolor: "primary",
      icon: <i className="ri-wallet-3-line"></i>,
    });
    sharedIndex += 1;

    // parents has not verified their accounts
    let unverifiedParents = data?.unverifiedUsersCount
      .filter((d) => [RoleEnum.PARENT].includes(d.role as RoleEnum))
      .reduce((a, c) => Number(a) + Number(c.count), 0);

    parentWidgets.push({
      id: sharedIndex,
      label: t("parentUnverifiedAccountsTitle"),
      description: t("parentUnverifiedAccountsDescription"),
      counter: unverifiedParents,
      link: "null",
      linkCaption: "null",
      bgcolor: "danger",
      icon: <i className="ri-forbid-line"></i>,
    });
    sharedIndex += 1;

    setParentWidgets(parentWidgets);

    const studentWidgets: any[] = [];

    // all students
    data?.usersCount.forEach((value: UserRoleWiseCount) => {
      if ([RoleEnum.STUDENT].includes(value.role as RoleEnum)) {
        studentWidgets.push({
          id: sharedIndex,
          label: t("allStudents"),
          description: t("allStudents"),
          counter: value.count,
          link: "null",
          linkCaption: "null",
          bgcolor: "secondary",
          icon: <i className="ri-group-line"></i>,
        });
        sharedIndex += 1;
      }
    });

    // students created by parent
    studentWidgets.push({
      id: sharedIndex,
      label: t("studentByParentTitle"),
      description: t("studentByParentDescription"),
      counter: data.studentsByParent,
      link: "null",
      linkCaption: "null",
      bgcolor: "secondary",
      icon: <i className="ri-group-line"></i>,
    });
    sharedIndex += 1;

    // students created by parent and made purchase
    studentWidgets.push({
      id: sharedIndex,
      label: t("studentByParentHavePurchaseTitle"),
      description: t("studentByParentHavePurchaseDescription"),
      counter: data.studentsByParentHavingPurchase,
      link: "null",
      linkCaption: "null",
      bgcolor: "primary",
      icon: <i className="ri-wallet-3-line"></i>,
    });
    sharedIndex += 1;

    // student accounts
    studentWidgets.push({
      id: sharedIndex,
      label: t("selfRegisteredStudentsTitle"),
      description: t("selfRegisteredStudentsDescription"),
      counter: data.selfRegisteredStudents,
      link: "null",
      linkCaption: "null",
      bgcolor: "success",
      icon: <i className="ri-group-line"></i>,
    });
    sharedIndex += 1;

    // student accounts with purchase
    // self registered students with purchase
    studentWidgets.push({
      id: sharedIndex,
      label: t("selfRegisteredStudentsWithPurchaseTitle"),
      description: t("selfRegisteredStudentsWithPurchaseDescription"),
      counter: data.selfRegisteredStudentsHavingPurchase,
      link: "null",
      linkCaption: "null",
      bgcolor: "success",
      icon: <i className="ri-wallet-3-line"></i>,
    });
    sharedIndex += 1;

    // student accounts has not verified
    let unverifiedStudents = data?.unverifiedUsersCount
      .filter((d) => [RoleEnum.STUDENT].includes(d.role as RoleEnum))
      .reduce((a, c) => Number(a) + Number(c.count), 0);

    studentWidgets.push({
      id: sharedIndex,
      label: t("unverifiedStudentsTitle"),
      description: t("unverifiedStudentsDescription"),
      counter: unverifiedStudents,
      link: "null",
      linkCaption: "null",
      bgcolor: "danger",
      icon: <i className="ri-forbid-line"></i>,
    });
    sharedIndex += 1;

    setStudentWidgets(studentWidgets);

    //#endregion

    //#region widgets 1

    const otherWidgets: any[] = [];

    // purchase made via website
    data?.userPlatformPurchase.forEach((value: UserPlatformWiseCount) => {
      if ([APP_PLATFORM.WEB_PORTAL].includes(value.platform as APP_PLATFORM)) {
        otherWidgets.push({
          id: sharedIndex,
          label: t("purchasesViaWebsiteTitle"),
          description: t("purchasesViaWebsiteDescription"),
          counter: value.count,
          link: "null",
          linkCaption: "null",
          bgcolor: "success",
          icon: <i className="ri-wallet-3-line"></i>,
        });
        sharedIndex += 1;
      }
    });

    // purchase made from parent account
    otherWidgets.push({
      id: sharedIndex,
      label: t("purchasesByParentsTitle"),
      description: t("purchasesByParentsDescription"),
      counter: data.purchasesFromParentAccount,
      link: "null",
      linkCaption: "null",
      bgcolor: "success",
      icon: <i className="ri-wallet-3-line"></i>,
    });
    sharedIndex += 1;

    // purchase made from student account
    otherWidgets.push({
      id: sharedIndex,
      label: t("purchasesByStudentsTitle"),
      description: t("purchasesByStudentsDescription"),
      counter: data.purchasesFromStudentAccount,
      link: "null",
      linkCaption: "null",
      bgcolor: "success",
      icon: <i className="ri-wallet-3-line"></i>,
    });
    sharedIndex += 1;

    // parent account created via website
    data?.accountsByPlatform.forEach((value: UserRolePlatformWiseCount) => {
      if (
        [APP_PLATFORM.WEB_PORTAL].includes(value.platform as APP_PLATFORM) &&
        [RoleEnum.PARENT].includes(value.role as RoleEnum)
      ) {
        otherWidgets.push({
          id: sharedIndex,
          label: t("parentsRegisteredViaWebsiteTitle"),
          description: t("parentsRegisteredViaWebsiteDescription"),
          counter: value.count,
          link: "null",
          linkCaption: "null",
          bgcolor: "success",
          icon: <i className="ri-parent-line"></i>,
        });
        sharedIndex += 1;
      }
    });

    // parent account created via app
    let parentAccountsByApp = data?.accountsByPlatform
      .filter(
        (d) =>
          [APP_PLATFORM.ANDROID_APP, APP_PLATFORM.IOS_APP].includes(
            d.platform as APP_PLATFORM,
          ) && [RoleEnum.PARENT].includes(d.role as RoleEnum),
      )
      .reduce((a, c) => Number(a) + Number(c.count), 0);

    otherWidgets.push({
      id: sharedIndex,
      label: t("parentsRegisteredViaAppTitle"),
      description: t("parentsRegisteredViaAppDescription"),
      counter: parentAccountsByApp,
      link: "null",
      linkCaption: "null",
      bgcolor: "success",
      icon: <i className="ri-parent-line"></i>,
    });
    sharedIndex += 1;

    // student account created via website
    data?.accountsByPlatform.forEach((value: UserRolePlatformWiseCount) => {
      if (
        [APP_PLATFORM.WEB_PORTAL].includes(value.platform as APP_PLATFORM) &&
        [RoleEnum.STUDENT].includes(value.role as RoleEnum)
      ) {
        otherWidgets.push({
          id: sharedIndex,
          label: t("studentsRegisteredViaWebsiteTitle"),
          description: t("studentsRegisteredViaWebsiteDescription"),
          counter: value.count,
          link: "null",
          linkCaption: "null",
          bgcolor: "success",
          icon: <i className="ri-group-line"></i>,
        });
        sharedIndex += 1;
      }
    });

    // student account created via app
    let studentAccountsFromApp = data?.accountsByPlatform
      .filter(
        (d) =>
          [APP_PLATFORM.ANDROID_APP, APP_PLATFORM.IOS_APP].includes(
            d.platform as APP_PLATFORM,
          ) && [RoleEnum.STUDENT].includes(d.role as RoleEnum),
      )
      .reduce((a, c) => Number(a) + Number(c.count), 0);

    otherWidgets.push({
      id: sharedIndex,
      label: t("studentsRegisteredViaAppTitle"),
      description: t("studentsRegisteredViaAppDescription"),
      counter: studentAccountsFromApp,
      link: "null",
      linkCaption: "null",
      bgcolor: "success",
      icon: <i className="ri-group-line"></i>,
    });
    sharedIndex += 1;

    // parent account created from website with at least 1 student
    data?.parentsHavingChildrenByPlatform.forEach(
      (value: UserPlatformWiseCount) => {
        if (
          [APP_PLATFORM.WEB_PORTAL].includes(value.platform as APP_PLATFORM)
        ) {
          otherWidgets.push({
            id: sharedIndex,
            label: t("parentsRegisteredViaWebsiteHaveChildrenTitle"),
            description: t(
              "parentsRegisteredViaWebsiteHaveChildrenDescription",
            ),
            counter: value.count,
            link: "null",
            linkCaption: "null",
            bgcolor: "success",
            icon: <i className="ri-parent-line"></i>,
          });
          sharedIndex += 1;
        }
      },
    );

    // parents accounts created from website has not verified
    data?.unverifiedUsersCount.forEach((value: UserRolePlatformWiseCount) => {
      if (
        [APP_PLATFORM.WEB_PORTAL].includes(value.platform as APP_PLATFORM) &&
        [RoleEnum.PARENT].includes(value.role as RoleEnum)
      ) {
        otherWidgets.push({
          id: sharedIndex,
          label: t("unverifiedParentsRegisteredViaWebTitle"),
          description: t("unverifiedParentsRegisteredViaWebDescription"),
          counter: value.count,
          link: "null",
          linkCaption: "null",
          bgcolor: "danger",
          icon: <i className="ri-forbid-line"></i>,
        });
        sharedIndex += 1;
      }
    });

    // parent account created via app with at least 1 student
    let parentsHavingChildrenByApp = data?.parentsHavingChildrenByPlatform
      .filter((d) =>
        [APP_PLATFORM.ANDROID_APP, APP_PLATFORM.IOS_APP].includes(
          d.platform as APP_PLATFORM,
        ),
      )
      .reduce((a, c) => Number(a) + Number(c.count), 0);

    otherWidgets.push({
      id: sharedIndex,
      label: t("parentsRegisteredViaAppHaveChildrenTitle"),
      description: t("parentsRegisteredViaAppHaveChildrenDescription"),
      counter: parentsHavingChildrenByApp,
      link: "null",
      linkCaption: "null",
      bgcolor: "success",
      icon: <i className="ri-parent-line"></i>,
    });
    sharedIndex += 1;

    // parent accounts created via app has not verified
    let unverifiedParentsFromApp = data?.unverifiedUsersCount
      .filter(
        (d) =>
          [APP_PLATFORM.ANDROID_APP, APP_PLATFORM.IOS_APP].includes(
            d.platform,
          ) && [RoleEnum.PARENT].includes(d.role as RoleEnum),
      )
      .reduce((a, c) => Number(a) + Number(c.count), 0);

    otherWidgets.push({
      id: sharedIndex,
      label: t("unverifiedParentsRegisteredViaAppTitle"),
      description: t("unverifiedParentsRegisteredViaAppDescription"),
      counter: unverifiedParentsFromApp,
      link: "null",
      linkCaption: "null",
      bgcolor: "danger",
      icon: <i className="ri-forbid-line"></i>,
    });
    sharedIndex += 1;

    setOtherWidgets(otherWidgets);

    //#endregion widgets 1
  };

  return (
    <React.Fragment>
      {!isSuperAdmin ? (
        <div className="page-content">
          <Container fluid>
            <BreadCrumb title={t("dashboard")} pageTitle={t("dashboard")} />
            <Row className="justify-content-center">
              <Col lg={8} xl={6}>
                <Card className="border-0 shadow-none">
                  <CardBody className="text-center py-5 px-4">
                    <div
                      className="avatar-lg mx-auto mb-4"
                      style={{
                        width: 72,
                        height: 72,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "rgba(28, 132, 238, 0.12)",
                      }}
                    >
                      <i
                        className="ri-bar-chart-box-line text-primary"
                        style={{ fontSize: "2rem" }}
                      ></i>
                    </div>
                    <h4 className="mb-2">
                      {t("dashboardAnalyticsComingSoonTitle", {
                        defaultValue: "Analytics coming soon",
                      })}
                    </h4>
                    <p
                      className="text-muted mb-0 mx-auto"
                      style={{ maxWidth: 420 }}
                    >
                      {t("dashboardAnalyticsComingSoonDescription", {
                        defaultValue:
                          "Dashboard analytics are on the way. Check back soon for insights tailored to your account.",
                      })}
                    </p>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Container>
        </div>
      ) : isLoadingAdminAnalytics == true && isFirstLoad === false ? (
        <div
          style={{
            justifyContent: "center",
            alignItems: "center",
            display: "flex",
            height: "100vh",
          }}
        >
          <Spinner
            size="sm"
            className="flex-shrink-0 m-5"
            role="status"
          ></Spinner>
        </div>
      ) : (
        <div className="page-content">
          <Container fluid>
            <BreadCrumb title={t("dashboard")} pageTitle={t("dashboard")} />
            <Row>
              <Col>
                <div className="h-100">
                  <Section
                    showWelcomeMessage="true"
                    value={dateValue}
                    onChange={(value: any) => onChangeDate(value)}
                  />
                  <Button
                    color="success"
                    className="mb-2"
                    onClick={() => {
                      getAdminAnalyticsApi(query);
                    }}
                  >
                    {t("refresh")}
                  </Button>
                  <Row>
                    <Widget data={amountWidgets} />
                  </Row>
                  <hr />
                  <Row>
                    <Widget data={parentWidgets} />
                  </Row>
                  <hr />
                  <Row>
                    <Widget data={studentWidgets} />
                  </Row>
                  <hr />
                  <Row>
                    <Widget data={otherWidgets} />
                  </Row>
                  <hr />
                  <Row>
                    <Col md={6}>
                      <Card className="card-height-100">
                        <CardHeader className="align-items-center d-flex">
                          <h4 className="card-title mb-0 flex-grow-1">
                            {t("amount")}
                          </h4>
                        </CardHeader>
                        <CardBody className="p-0">
                          {!isLoadingAdminAnalytics && dataAdminAnalytics && (
                            <SubscriptionsByCourseGradeBarChart
                              data={
                                dataAdminAnalytics?.courseGradeWiseSubscription
                              }
                            />
                          )}
                        </CardBody>
                      </Card>
                    </Col>
                  </Row>
                </div>
              </Col>
            </Row>
          </Container>
        </div>
      )}
    </React.Fragment>
  );
};

export default DashboardPage;

const SubscriptionsByCourseGradeBarChart = ({
  data,
}: {
  data: CourseGradeWiseSubscriptions[];
}) => {
  const [filter, setFilter] = useState<"all" | "first" | "second">("first");
  const { t, i18n } = useTranslation();

  const { layoutModeType } = useSelector((state: any) => ({
    layoutModeType: state.Layout.layoutModeType,
  }));

  const filteredData = useMemo(() => {
    if (filter === "first") {
      return data.filter((d: any) => d.course.name.en.includes("First Course"));
    } else if (filter === "second") {
      return data.filter((d: any) =>
        d.course.name.en.includes("Second Course"),
      );
    }
    return data;
  }, [filter, data]);

  const series = [
    {
      name: t("amount"),
      data: filteredData.map((d: CourseGradeWiseSubscriptions) =>
        parseFloat(d.amount as any),
      ),
    },
    {
      name: t("realMoney"),
      data: filteredData.map((d: CourseGradeWiseSubscriptions) =>
        parseFloat(d.dueDeposit as any),
      ),
    },
  ];

  const options = {
    chart: {
      type: "bar",
      height: 636,
      toolbar: { show: true },
    },
    plotOptions: {
      bar: {
        borderRadius: 4,
        horizontal: true,
        dataLabels: { position: "top" },
      },
    },
    dataLabels: {
      enabled: true,
      offsetX: 32,
      style: {
        fontWeight: 400,
        colors: [layoutModeType === "white" ? "#000" : "#fff"],
      },
    },
    legend: { show: true },
    grid: { show: false },
    xaxis: {
      categories: filteredData.map((d: any) => {
        if (["first", "second"].includes(filter)) {
          return i18n.language === "ar"
            ? `${d.grade.name.ar}`
            : `${d.grade.name.en}`;
        } else {
          return i18n.language === "ar"
            ? `${d.course.name.ar} - ${d.grade.name.ar}`
            : `${d.course.name.en} - ${d.grade.name.en}`;
        }
      }),
    },
    colors: ["#1c84ee", "#11c46e"],
  };

  return (
    <React.Fragment>
      <div className="my-2 ms-2">
        <Button
          color={filter === "all" ? "primary" : "secondary"}
          size="sm"
          className="me-1"
          onClick={() => setFilter("all")}
        >
          {t("all")}
        </Button>
        <Button
          color={filter === "first" ? "primary" : "secondary"}
          size="sm"
          className="me-1"
          onClick={() => setFilter("first")}
        >
          {t("firstSemester")}
        </Button>
        <Button
          className="me-1"
          color={filter === "second" ? "primary" : "secondary"}
          size="sm"
          onClick={() => setFilter("second")}
        >
          {t("secondSemester")}
        </Button>
      </div>
      <div dir="ltr">
        <ReactApexChart
          // @ts-ignore
          options={options}
          series={series}
          type="bar"
          height="636"
          className="apex-charts"
        />
      </div>
    </React.Fragment>
  );
};
